import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { get, getDatabase, ref } from 'firebase/database'
import fbConf from '../../FirebaseConf'
import PageTitle from '../../components/PageTitle'
import LoaderDots from '../../components/LoaderDots'
import ButtonWithIcon from '../../components/ButtonWithIcon'
import Dropdown from '../../components/Dropdown'
import Icon from '../../Icon'
import Table from '../../modules/Table'
import { TableCellTypes } from '../../enums/TableCellTypes'
import VoltieUser from '../../types/VoltieUser'
import { useNotification } from '../../modules/Notification'
import { StatBar, StatTile, StatIconChip, StatValue, StatLabel, ToolbarContainer, RefreshTimerLabel } from './userStyles'
import { providerMeta, approxCreatedMs, approxLastOnlineMs, chargerCount, normalizeUser, dateTimeParts } from './userHelpers'

interface UserSummary {
  uid: string
  name: string
  email: string
  provider: string // Apple | Google | Email
  providerIcon: string
  createdMs: number | null
  lastOnlineMs: number | null
  chargers: number
  faulty: boolean // malformed node (missing email/user_uid) — the "audit" bucket
  active30: boolean // seen online within the last 30 days
}

type UserFilter = 'all' | 'withCharger' | 'faulty' | 'active30'

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000

function providerBucket(providerEnum?: string): string {
  switch ((providerEnum || '').toLowerCase()) {
    case 'apple': return 'Apple'
    case 'google': case 'gmail': return 'Google'
    default: return 'Email'
  }
}

export default function Users() {
  const { t } = useTranslation()
  const { addNotification } = useNotification()

  const [users, setUsers] = useState<UserSummary[] | null>(null)
  const [activeFilter, setActiveFilter] = useState<UserFilter>('all')
  const [lastRefresh, setLastRefresh] = useState<string>('')
  const [triggerRefresh, setTriggerRefresh] = useState<number>(0)

  async function fetchUsers() {
    setUsers(null)
    try {
      // Admin reads the whole chargerusers node once (rules allow it); reduce to a compact summary in memory.
      const snap = await get(ref(getDatabase(fbConf), 'voltie/chargerusers'))
      const val: { [uid: string]: VoltieUser } | null = snap.val()
      const now = Date.now()
      const summaries: UserSummary[] = []
      for (const uid in val) {
        const raw = val[uid]
        const faulty = !raw || typeof raw !== 'object' || !raw.email || !raw.user_uid
        const u = normalizeUser(raw || {})
        const lastOnlineMs = approxLastOnlineMs(u)
        summaries.push({
          uid,
          // Name only (no email fallback) so the row shows "-" when the user has no name.
          // Trim every part: a whitespace-only nickname/name is truthy and would render as a blank cell.
          name: ([u.first_name, u.last_name].filter(Boolean).join(' ').trim() || (u.nickname || '').trim()),
          email: u.email || '-',
          provider: providerBucket(u.provider_enum),
          providerIcon: providerMeta(u.provider_enum).icon,
          createdMs: approxCreatedMs(u),
          lastOnlineMs,
          chargers: chargerCount(u),
          faulty,
          active30: lastOnlineMs != null && now - lastOnlineMs <= THIRTY_DAYS_MS,
        })
      }
      summaries.sort((a, b) => a.email.localeCompare(b.email))
      setUsers(summaries)
      setLastRefresh(new Date().toString().split(' ')[4])
      setTriggerRefresh(prev => prev + 1)
    } catch (e) {
      // Never hang on the loader: surface the error and recover to an empty list.
      addNotification(`${e}`, 'TEXT', 'error')
      setUsers([])
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const all = users || []
  const withChargerCount = all.filter(u => u.chargers > 0).length
  const faultyCount = all.filter(u => u.faulty).length
  const activeCount = all.filter(u => u.active30).length

  const matches = (u: UserSummary, f: UserFilter): boolean => {
    switch (f) {
      case 'withCharger': return u.chargers > 0
      case 'faulty': return u.faulty
      case 'active30': return u.active30
      default: return true
    }
  }
  const displayed = all.filter(u => matches(u, activeFilter))

  // Clicking a tile applies its filter; clicking the active tile again clears back to "all".
  const applyFilter = (f: UserFilter) => {
    setActiveFilter(prev => (prev === f ? 'all' : f))
    setTriggerRefresh(prev => prev + 1)
  }

  // Decouple stable filter values from their (translatable) labels — reuse the stat labels.
  const FILTERS: { value: UserFilter; label: string }[] = [
    { value: 'all', label: t('users_statTotal') },
    { value: 'withCharger', label: t('users_statWithCharger') },
    { value: 'faulty', label: t('users_statErrorNodes') },
    { value: 'active30', label: t('users_statActive30d') },
  ]
  const currentFilterLabel = FILTERS.find(f => f.value === activeFilter)?.label || t('users_statTotal')

  const filterSlot = (
    <Dropdown
      title={{ icon: 'general.filter', label: currentFilterLabel }}
      options={FILTERS.map(f => ({ label: f.label }))}
      handleChoose={(label) => { const f = FILTERS.find(x => x.label === label); if (f) { setActiveFilter(f.value); setTriggerRefresh(prev => prev + 1) } }}
    />
  )

  return (
    <>
      <PageTitle label={t('menu_users')} />

      <ToolbarContainer>
        <div>
          <ButtonWithIcon icon="general.refresh" label={t('charger_refresh')} onClick={fetchUsers} backgroundColor="success" iconColor="white" />
          <RefreshTimerLabel>{lastRefresh}</RefreshTimerLabel>
        </div>
      </ToolbarContainer>

      <StatBar>
        <StatTile $clickable $active={activeFilter === 'all'} role="button" aria-pressed={activeFilter === 'all'} onClick={() => applyFilter('all')}>
          <StatIconChip><Icon path="menu.users" width="20px" height="20px" color="primaryActive" /></StatIconChip>
          <div><StatValue>{users ? users.length : <LoaderDots />}</StatValue><StatLabel>{t('users_statTotal')}</StatLabel></div>
        </StatTile>
        <StatTile $clickable $tone="success" $active={activeFilter === 'withCharger'} role="button" aria-pressed={activeFilter === 'withCharger'} onClick={() => applyFilter('withCharger')}>
          <StatIconChip $tone="success"><Icon path="general.charger" width="20px" height="20px" color="successActive" /></StatIconChip>
          <div><StatValue>{users ? withChargerCount : <LoaderDots />}</StatValue><StatLabel>{t('users_statWithCharger')}</StatLabel></div>
        </StatTile>
        <StatTile $clickable $tone="danger" $active={activeFilter === 'faulty'} role="button" aria-pressed={activeFilter === 'faulty'} onClick={() => applyFilter('faulty')}>
          <StatIconChip $tone="danger"><Icon path="general.warning" width="20px" height="20px" color="dangerActive" /></StatIconChip>
          <div><StatValue $tone="danger">{users ? faultyCount : <LoaderDots />}</StatValue><StatLabel>{t('users_statErrorNodes')}</StatLabel></div>
        </StatTile>
        <StatTile $clickable $tone="warning" $active={activeFilter === 'active30'} role="button" aria-pressed={activeFilter === 'active30'} onClick={() => applyFilter('active30')}>
          <StatIconChip $tone="warning"><Icon path="chargers.time" width="20px" height="20px" color="warningActive" /></StatIconChip>
          <div><StatValue>{users ? activeCount : <LoaderDots />}</StatValue><StatLabel>{t('users_statActive30d')}</StatLabel></div>
        </StatTile>
      </StatBar>

      {users ? (
        <Table
          id="Users"
          height="calc(90vh - 265px)"
          isSearchEnabled={true}
          isPaginationEnabled={true}
          searchBarSlot={filterSlot}
          isRenderEmpty={displayed.length === 0}
          triggerRefresh={triggerRefresh}
          columns={[
            { width: '44px' },
            { label: t('user_providersEmail') },
            { label: t('user_created'), width: '13%' },
            { label: t('user_lastOnline'), width: '13%' },
            { label: t('users_colChargerCount'), width: '13%' },
            { width: '44px' },
          ]}
          data={displayed.length > 0 ? displayed.map(u => [
            {
              link: `/users/${u.uid}`,
              custom: <div style={{ paddingLeft: '14px', display: 'flex' }}><Icon path={u.providerIcon} width="18px" height="18px" /></div>,
            }, {
              link: `/users/${u.uid}`,
              sortBy: u.email,
              value: { type: TableCellTypes.LabelDivided, value1: u.name.trim() || '-', value2: u.email },
            }, {
              link: `/users/${u.uid}`,
              sortBy: u.createdMs || 0,
              value: { type: TableCellTypes.LabelDivided, value1: dateTimeParts(u.createdMs).date, value2: dateTimeParts(u.createdMs).time },
            }, {
              link: `/users/${u.uid}`,
              sortBy: u.lastOnlineMs || 0,
              value: { type: TableCellTypes.LabelDivided, value1: dateTimeParts(u.lastOnlineMs).date, value2: dateTimeParts(u.lastOnlineMs).time },
            }, {
              link: `/users/${u.uid}`,
              sortBy: u.chargers,
              value: { type: TableCellTypes.Label, value: u.chargers.toString() },
            }, {
              link: `/users/${u.uid}`,
              value: { type: TableCellTypes.OpenButton },
            },
          ]) : [[{}, { sortBy: '' }, { sortBy: 0 }, { sortBy: 0 }, { sortBy: 0 }, {}]]}
        />
      ) : (
        <div style={{ textAlign: 'center', marginTop: '80px' }}><LoaderDots /></div>
      )}
    </>
  )
}
